import React from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Snackbar,
  Alert,
  Divider,
  Grid,
  Avatar,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  useTheme,
  CircularProgress,
} from "@mui/material";

import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ModeButtons from "../../Home/components/ModeButtons";

import textFieldStyle from "../styles/styles";
import { useProfile } from "../hooks/useProfile";
import { managementPageShellSx } from "../../shared/managementPageLayout";
import PageCloseButton from "../../shared/PageCloseButton";

const profileScrollSx = managementPageShellSx.scrollContent;

export default function ProfilePanel() {
  const theme = useTheme();
  const {
    user,
    loading,
    profileFile,
    avatarSrc,
    profileUpload,
    uploadingProfile,
    deletingProfile,
    formData,
    tab,
    snackbar,
    isAdmin,
    roleLabel,
    hasProfileImage,
    handleSave,
    handleTabChange,
    handleFormDataChange,
    handleProfileFileChange,
    handleProfileUpload,
    handleProfileDelete,
    handleSnackbarClose,
  } = useProfile();

  const IconComponent = isAdmin ? AdminPanelSettingsIcon : PersonIcon;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          flexShrink: 0,
        }}
      >
        <ModeButtons />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "stretch",
          overflow: "hidden",
          px: 2,
          pt: { xs: 2, md: 3 },
          pb: { xs: 8, md: 7 },
          boxSizing: "border-box",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 4,
            maxWidth: 700,
            width: "100%",
            height: "100%",
            maxHeight: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            background: "rgba(255,255,255,0.0)",
            border: "1px solid rgba(255,255,255,0.2)",
            color: "white",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ flexShrink: 0, position: "relative" }}>
          <Typography
            variant="h5"
            fontWeight={700}
            align="center"
            sx={{ mb: 2 }}
          >
            <PersonIcon sx={{ mr: 1 }} />
            Profile Panel
          </Typography>
          <PageCloseButton sx={managementPageShellSx.headerCloseButton} />

          <Divider sx={{ borderColor: "rgba(255,255,255,0.2)" }} />
          </Box>

          {loading && (
            <Box
              sx={{
                ...profileScrollSx,
              display: "flex",
              justifyContent: "center",
                alignItems: "center",
                pt: 2, }}
            >
              <CircularProgress size={40} />
            </Box>
          )}

          {!loading && user && (
            <Box sx={{ ...profileScrollSx, pt: 2 }}>
              <Box textAlign="center" mb={2}>
                <Avatar
                  src={avatarSrc}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: theme.palette.error.main,
                    mx: "auto",
                    mb: 1,
                  }}
                >
                  <IconComponent sx={{ fontSize: 40 }} />
                </Avatar>

                <Typography variant="h6" fontWeight={600}>
                  {user.name}
                </Typography>

                <Chip
                  label={roleLabel}
                  size="small"
                  sx={{
                    mt: 1,
                    background: "rgba(255,255,255,0.2)",
                    color: "white",
                  }}
                />
              </Box>

              <Tabs
                value={tab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  "& .MuiTab-root": {
                    color: "rgba(255,255,255,0.6)",
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "#fff",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#070202",
                    color: "white",
                    height: 3,
                    borderRadius: 2,
                  },
                }}
              >
                <Tab label="View Profile" />
                <Tab label="Edit Profile" />
              </Tabs>

              {tab === 0 && (
                <>
                  <Card
                    sx={{
                      mt: 3,
                      mb: 1.5,
                      background: "rgba(255,255,255,0.06)",
                      color: "white",
                    }}
                  >
                    <CardContent sx={{ display: "flex", gap: 1 }}>
                      <EmailIcon fontSize="small" />
                      <Typography variant="body2">{user.email}</Typography>
                    </CardContent>
                  </Card>

                  <Card
                    sx={{
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.15)",
                      color: "white",
                    }}
                  >
                    <CardContent sx={{ display: "flex", gap: 1 }}>
                      <CalendarTodayIcon fontSize="small" />
                      <Typography variant="body2">
                        Joined:{" "}
                        {user.joinedOn
                          ? new Date(user.joinedOn).toLocaleDateString()
                          : "N/A"}
                      </Typography>
                    </CardContent>
                  </Card>
                </>
              )}

              {tab === 1 && (
                <Box
                  sx={{
                    mt: 3,
                    p: 3,
                    borderRadius: 3,
                    background: "rgba(255,255,255,0.04)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                      mb: 3,
                    }}
                  >
                    <Avatar
                      src={avatarSrc}
                      sx={{
                        width: 92,
                        height: 92,
                        bgcolor: theme.palette.error.main,
                        border: "1px solid rgba(255,255,255,0.25)",
                      }}
                    >
                      <IconComponent sx={{ fontSize: 44 }} />
                    </Avatar>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        component="label"
                        startIcon={<PhotoCameraIcon />}
                        size="small"
                        sx={{
                          color: "#fff",
                          border: "1px solid rgba(255,255,255,0.45)",
                          px: 2,
                        }}
                      >
                        Choose Photo
                        <input
                          hidden
                          accept="image/*,.gif"
                          type="file"
                          onChange={handleProfileFileChange}
                        />
                      </Button>

                      <Button
                        size="small"
                        onClick={handleProfileUpload}
                        disabled={!profileFile || uploadingProfile || deletingProfile}
                        sx={{
                          background: "#6a0dad",
                          color: "#fff",
                          px: 2,
                          "&:hover": {
                            background: "#5a009d",
                          },
                          "&.Mui-disabled": {
                            color: "rgba(255,255,255,0.45)",
                            background: "rgba(255,255,255,0.1)",
                          },
                        }}
                      >
                        {uploadingProfile ? (
                          <CircularProgress size={18} sx={{ color: "#fff" }} />
                        ) : (
                          "Upload"
                        )}
                      </Button>

                      <Button
                        size="small"
                        startIcon={
                          deletingProfile ? (
                            <CircularProgress size={16} sx={{ color: "#fff" }} />
                          ) : (
                            <DeleteOutlineIcon />
                          )
                        }
                        onClick={handleProfileDelete}
                        disabled={
                          !hasProfileImage || uploadingProfile || deletingProfile
                        }
                        sx={{
                          color: "#fff",
                          border: "1px solid rgba(255,255,255,0.45)",
                          px: 2,
                          "&:hover": {
                            background: "rgba(211,47,47,0.85)",
                            borderColor: "rgba(211,47,47,0.85)",
                          },
                          "&.Mui-disabled": {
                            color: "rgba(255,255,255,0.45)",
                            borderColor: "rgba(255,255,255,0.18)",
                          },
                        }}
                      >
                        Delete
                      </Button>
                    </Box>

                    {(profileFile || profileUpload?.profileName) && (
                      <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
                        {profileFile?.name || profileUpload.profileName}
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    label="First Name"
                    fullWidth
                    size="small"
                    sx={textFieldStyle}
                    value={formData.firstName}
                    onChange={(e) =>
                      handleFormDataChange("firstName", e.target.value)
                    }
                    InputProps={{ sx: { color: "white" } }}
                  />

                  <TextField
                    label="Last Name"
                    fullWidth
                    size="small"
                    sx={textFieldStyle}
                    value={formData.lastName}
                    onChange={(e) =>
                      handleFormDataChange("lastName", e.target.value)
                    }
                    InputProps={{ sx: { color: "white" } }}
                  />

                  <TextField
                    label="Username"
                    fullWidth
                    size="small"
                    sx={textFieldStyle}
                    value={formData.username}
                    onChange={(e) =>
                      handleFormDataChange("username", e.target.value)
                    }
                    InputProps={{ sx: { color: "white" } }}
                  />

                  <TextField
                    label="Email"
                    fullWidth
                    size="small"
                    sx={textFieldStyle}
                    value={formData.email}
                    onChange={(e) =>
                      handleFormDataChange("email", e.target.value)
                    }
                    InputProps={{ sx: { color: "white" } }}
                  />

                  <Grid container spacing={2} justifyContent="center" mt={3}>
                    <Grid item>
                      <Button
                        startIcon={<SaveIcon />}
                        size="small"
                        onClick={handleSave}
                        sx={{
                          background: "#6a0dad",
                          color: "#fff",
                          px: 3,
                          "&:hover": {
                            background: "#5a009d",
                          },
                        }}
                      >
                        Save
                      </Button>
                    </Grid>

                    <Grid item>
                      <Button
                        startIcon={<CancelIcon />}
                        size="small"
                        onClick={() => handleTabChange(null, 0)}
                        sx={{
                          color: "#fff",
                          border: "1px solid #f0e4e4",
                          px: 3,
                          "&:hover": {
                            background: "red",
                            borderColor: "red",
                          },
                        }}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
          </Snackbar>
        </Paper>
      </Box>
    </Box>
  );
}
